import React, { useEffect, useRef, Fragment, } from 'react';
import { uniq, sortBy, pickBy, sumBy, orderBy, chunk, groupBy, get, pick, isEmpty, keyBy, omit, difference, union, } from 'lodash';
import numeral from 'numeral';
import { Input, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import qs from 'qs';
import { format as formatDate } from 'date-fns';
import { TextEncoder, } from 'text-encoding';
import { unparse as unparseCsv, } from 'papaparse';
import fileDownload from 'js-file-download';
import { useCounter, useList, } from 'react-use';
import { jsPDF } from 'jspdf';
import { PDFDocument, } from 'pdf-lib';

import firebase from '../../firebase';
import { batch } from '../../shared/firebase';
import { statuses, boxSizes, fields, receivingOptions, } from '../../shared/models/receivingRequest';
import { drawReceivementLabels, } from '../../label';
import { smallImageUrl, } from '../../shared/models/amazonProduct';
import { newId, fullPathWithParams } from '../../util';
import { idWithTenant, } from '../../shared/util';
import { fieldDisplayValue, } from '../../shared/util';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useQueryParams from '../hooks/useQueryParams';
import usePagination from '../hooks/usePagination';
import Page from '../hocs/Page';
import ModelFormModal from '../modals/ModelFormModal';
import ReceivingRequestFormModal from '../modals/ReceivingRequestFormModal';
import ReceivingRequestDetail from '../ReceivingRequestDetail';
import Field from '../Field';
import AddButton from '../AddButton';
import ModalButton from '../ModalButton';
import ImportButton from '../ImportButton';
import ProgressButton from '../ProgressButton';
import ExportButton from '../ExportButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import Pagination from '../Pagination';
// import QuerySortLabel from '../QuerySortLabel';
import QueryInput from '../QueryInput';
import QuerySelector from '../QuerySelector';
import ProductTagsDisplay from '../ProductTagsDisplay';

const countPerPage = 100;
const QuerySortLabel = 'span';
const { keys, values, entries } = Object;
const db = firebase.firestore();
const receivingOptionsByLabel = keyBy(entries(receivingOptions).map(([k, v]) => ({ key: k, ...v })), 'label');
const sortIterateees = {
  id: _ => _.createdAt.toDate(),
  documentNumber: _ => _.documentNumber || '',
  unknown: _ => (_.pendingItems || []).length,
  status: _ => keys(statuses).indexOf(_.status),
  receivedAt: _ => _.receivedAt ? _.receivedAt.toDate() : new Date(1900, 0, 1),
  scheduleDate: _ => _.scheduleDate ? _.scheduleDate.toDate() : new Date(1900, 0, 1),
  receivingOptionValues: _ => fieldDisplayValue(_.receivingOptionValues, fields().receivingOptionValues) || '',
  note: _ => _.note || '',
};
const statusOptions = entries(statuses).map(([k, v]) => ({ label: v.label, value: k }));
const searchTypes = {
  sku: {
    label: 'SKU',
  },
  id: {
    label: '入荷ID',
  },
};
const searchTypeOptions = entries(searchTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default Page(function ReceivingRequests (props) {
  const { tenant, location, history } = props;
  const [componentVersion, { inc: updateComponentVersion }] = useCounter(0);
  const queryParams = useQueryParams();
  const { 
    newProductId,
    sort = { field: 'id', direction: 'desc', },
    statuses: statusesForFilter = [],
    keyword,
  } = queryParams;
  const searchType = queryParams.searchType?.[0] ?? 'sku'
  const isSearching = !isEmpty(keyword);
  const addButtonRef = useRef();
  const receivingRequestsRef = tenant.ref.collection('receivingRequests');
  const productsRef = tenant.ref.collection('products');
  // NOTE: ここのrefをfilter等してしまうと、newIdがおかしくなることに注意
  const productChunks = useCollectionSubscription(tenant.ref.collection('productChunks'), [tenant.id]);
  const receivingRequestChunks = useCollectionSubscription(tenant.ref.collection('receivingRequestChunks'), [tenant.id], { initialItems: null });
  const hasLoadedReceivingRequestChunks = receivingRequestChunks != null;
  const chunkProducts = productChunks.flatMap(_ => entries(omit(_, ['id', 'ref'])).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('products').doc(k), ...v, })));
  const chunkReceivingRequests = orderBy(receivingRequestChunks?.flatMap(_ => entries(_.data || {}).map(([k, v]) => ({ id: k, ref: tenant.ref.collection('receivingRequests').doc(k), ...v, }))), _ => numeral(_.id).value(), 'desc');
  const products = chunkProducts;
  const productsById = keyBy(products, 'id');
  const productsByExtraSellerIdAndSku = keyBy(products, _ => [_.extraSellerId, _.sku].join('__'));
  const extraSellers = useCollectionSubscription(tenant.ref.collection('extraSellers').orderBy('createdAt'), [tenant.id]);
  const extraSellersByName = keyBy([{ ...tenant, name: 'メイン', id: null, }, ...extraSellers], 'name');
  const [selectedItemIds, { set: setSelectedItems, push: selectItem, removeAt: removeSelectedItemAt }] = useList([]);
  const unselectItem = _ => removeSelectedItemAt(selectedItemIds.indexOf(_));
  const isSelecting = selectedItemIds.length > 0;

  // NOTE: 絞り込み
  let filteredChunkReceivingRequests = chunkReceivingRequests;
  if(isSearching) {
    filteredChunkReceivingRequests = ({
      sku: _ => filteredChunkReceivingRequests.filter(_ => (_.productIds || []).map(_ => productsById[_]?.sku || '').some(_ => _.includes(keyword))),
      id: _ => filteredChunkReceivingRequests.filter(_ => [_.id,  _.specifiedId].includes(keyword.match(/(W\d+)/)?.[1])),
    })[searchType]();
  } else {
    if(!isEmpty(statusesForFilter)) {
      filteredChunkReceivingRequests = filteredChunkReceivingRequests.filter(_ => statusesForFilter.includes(_.status));
    }
  }
  const pagination = usePagination({ countPerPage, totalCount: filteredChunkReceivingRequests.length, });
  const { pageNumber, offset, } = pagination;
  const { items: receivingRequests, } = useDocumentsFetch(filteredChunkReceivingRequests.slice(offset, offset + countPerPage).map(_ => _.ref), [tenant, receivingRequestChunks, productChunks, pageNumber, componentVersion, statusesForFilter.join(''), searchType, keyword], { detail: true });
  const receivingRequestsById = keyBy(receivingRequests, 'id');
  const sortedReceivingRequestItems = orderBy(receivingRequests.filter(_ => _.createdAt), (sortIterateees[sort.field] || sort.field), sort.direction);
  const displayedItemIds = sortedReceivingRequestItems.map(_ => _.id);
  const isAllDisplayedSelected = displayedItemIds.length > 0 && difference(displayedItemIds, selectedItemIds).length === 0;
  const [lastReceivingRequest] = chunkReceivingRequests;
  const newRef = hasLoadedReceivingRequestChunks && receivingRequestsRef.doc('W' + newId(lastReceivingRequest));

  const onClickDownloadSampleCsv = () => {
    const data = [{
      ...(tenant.tenantOptionValues?.includes('multiSellers') && { ショップ識別名: 'メイン' }),
      SKU: 'sku_123456',
      数量: 1,
      伝票番号: '111122223333',
      オプション: '追加検品,セット',
      検品依頼内容: '割れていないか要確認',
      セット内容: 'AとBを1つずつ',
    }];
    const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
    const content = encoder.encode(unparseCsv(data));
    fileDownload(content, '入荷依頼_サンプル.csv');
  };
  const rowsForExport = () => sortedReceivingRequestItems.map((receivingRequestItem) => {
    const { id, ref, pendingItems = [], items = [], documentNumber, status, note, tenantNote, files = [], slipImages = [], janImages = [], receivingOptionValues = [], checkNote, setNote, viaCancel = false, createdAt, receivedAt, scheduleDate, isReceivedByUser = false, } = receivingRequestItem;
    const _items = items.map((item) => {
      const { productId } = item;
      const product = productsById[productId];
      const goodInventoryIds = receivingRequestItem[`goodInventoryIds-${productId}`] || [];
      const ngInventoryIds = receivingRequestItem[`ngInventoryIds-${productId}`] || [];
      return {
        ...item,
        product,
        goodInventoryIds,
        ngInventoryIds,
      };
    });
    return {
      入荷依頼ID: idWithTenant(ref),
      入荷依頼日時: formatDate(createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss'),
      入庫予定日: scheduleDate && formatDate(scheduleDate.toDate(), 'yyyy/MM/dd'),
      ステータス: statuses[status]?.label,
      入庫日: receivedAt && formatDate(receivedAt.toDate(), 'yyyy/MM/dd'),
      伝票番号: documentNumber,
      SKU: _items.map(_ => _.product?.sku).join(','),
      商品名: _items.map(_ => _.product?.name).join(','),
      入荷依頼数: _items.map(_ => _.quantity).join(','),
      入庫数: _items.map(_ => _.goodInventoryIds?.length).join(','),
      販売不可数: _items.map(_ => _.ngInventoryIds?.length).join(','),
      オプション: fieldDisplayValue(receivingOptionValues, fields().receivingOptionValues),
      検品依頼: checkNote,
      セット内容: setNote,
      連絡事項: note,
      メモ: tenantNote,
    };
  });
  const validate = async (row, i) => {
    const {
      ショップ識別名: extraSellerName,
      SKU: sku,
      数量: _quantity,
      伝票番号: documentNumber,
      オプション: _receivingOptionValues,
      検品依頼内容: checkNote,
      セット内容: setNote,
    } = row;
    const extraSeller = extraSellersByName[extraSellerName || 'メイン'];
    const product = productsByExtraSellerIdAndSku[[extraSeller?.id, sku].join('__')];
    const quantity = parseInt(_quantity, 10);
    const receivingOptionValues = _receivingOptionValues.replace(/\s　/g, '').split(',').filter(_ => _).map(_ => get(receivingOptionsByLabel, [_, 'key']));
    const documentNumberUniqueness = documentNumber || i.toString();
    const errors = [
      !extraSeller && 'ショップ識別名が正しくありません',
      !product && 'SKUが正しくありません',
      !(quantity > 0) && '数量が正しくありません',
      receivingOptionValues.some(_ => !_) && 'オプションが正しくありません',
    ];
    return {
      source: row,
      errors: errors.filter(_ => _),
      extraSellerId: extraSeller?.id,
      productId: product?.id,
      quantity,
      documentNumber,
      documentNumberUniqueness,
      receivingOptionValues,
      checkNote,
      setNote,
    }
  };
  const processUpdateRows = async (rows) => {
    await batch(db, rows.filter(_ => _['入荷依頼ID']?.includes('-')), (batch, row) => {
      batch.update(receivingRequestsRef.doc(row['入荷依頼ID'].split('-')[1]), {
        documentNumber: row['伝票番号'],
        updatedAt: new Date(),
      });
    });
  };
  const processRows = async (rows) => {
    const validatedRows = (await chunk(rows.filter(_ => _['SKU']), 10)
      .reduce(async (x, rows, chunkIndex) => {
        const prevs = await x;
        return [...prevs, (await Promise.all(rows.map((row, i) => validate(row, chunkIndex * 10 + i))))];
      }, Promise.resolve([])))
      .flat();
    if(validatedRows.filter(_ => !isEmpty(_.errors)).length > 0) {
      const encoder = new TextEncoder('Shift_JIS', { NONSTANDARD_allowLegacyEncoding: true });
      const content = encoder.encode(unparseCsv(validatedRows.map((row) => {
        return {
          ...row.source,
          エラー内容: row.errors.join('\n'),
        };
      })));
      fileDownload(content, 'エラー結果.csv');
      throw new Error();
    } else {
      const groupedRows = groupBy(validatedRows, row => [
        'documentNumberUniqueness',
      ].map(_ => row[_]).join('--'));
      await batch(db, values(groupedRows), (batch, rows, i) => {
        const [row] = rows;
        const productIds = uniq(rows.map(_ => _.productId));
        const items = sortBy(
          entries(groupBy(rows, 'productId')).map(([productId, rows]) => ({ productId, quantity: sumBy(rows, 'quantity'), })),
          _ => productIds.indexOf(_.productId)
        );
        const id = 'W' + (parseInt(newId(lastReceivingRequest), 10) + i).toString();
        batch.set(receivingRequestsRef.doc(id), {
          ...omit(row, ['source', 'errors', 'productId', 'quantity', 'documentNumberUniqueness']),
          items,
          status: 'initial',
          productIds: items.map(_ => _.productId),
          createdAt: new Date(),
        });
      });
    }
  };
  const onClickPrintReceivementLabels = async () => {
    const allPdfDoc = await PDFDocument.create();
    const items = selectedItemIds.flatMap((selectedItemId) => {
      const receivingRequest = receivingRequestsById[selectedItemId]
      return receivingRequest.items.map((item) => {
        return {
          receivingRequest,
          ...item,
          product: productsById[item.productId],
        };
      });
    });
    await chunk(items, 6).reduce(async (x, items, i) => {
      await x;

      const canvas = document.createElement('canvas');
      const scale = 10;
      canvas.width = scale * 210;
      canvas.height = scale * 297;
      drawReceivementLabels(canvas, items, scale);
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      const doc = new jsPDF({
        format: [210, 297]
      });
      doc.addImage(imageData, 'JPEG', 0, 0, 210, 297);
      const pdfData = doc.output('datauri');
      const pdfDoc = await PDFDocument.load(pdfData);
      const [page] = await allPdfDoc.copyPages(pdfDoc, keys(pdfDoc.getPages()));
      await allPdfDoc.addPage(page);
    }, Promise.resolve());
    fileDownload(await allPdfDoc.save(), `入荷ラベル.pdf`);
  };
  useEffect(() => {
    if(isEmpty(statusesForFilter)) {
      const path = fullPathWithParams({ statuses: ['initial', 'unknown', 'pending', 'resubmitted', 'processing'], }, location);
      history.replace(encodeURI(path));
    }
  }, []);
  // DEV
  /*
  useEffect(() => {
    if(receivingRequests?.[0]) {
      const canvas = document.getElementById('canvas');
      const scale = 10;
      canvas.width = scale * 210;
      canvas.height = scale * 297;
      const items = receivingRequests.flatMap((receivingRequest) => {
        return receivingRequest.items.map((item) => {
          return {
            receivingRequest,
            ...item,
            product: productsById[item.productId],
          };
        });
      });
      drawReceivementLabels(canvas, items, scale);
    }
  }, [receivingRequests?.[0]]);
  */

  return (
    <div className="receiving-requests container-fluid">
      <div className="p-4 bg-white my-4">
        {
          // DEV
          // <canvas className="border" id="canvas" width={500} height={400} />
        }
        <div className="d-flex justify-content-start align-items-end mb-2 gap-1">
          <div className="card p-2">
            <div style={{ width: 300 }} >
              <QuerySelector className="w-100" paramName="statuses" isMulti options={statusOptions} label="ステータスで絞込み" disabled={isSearching} />
            </div>
          </div>
          <div className="card p-2 flex-row gap-1">
            <QuerySelector paramName="searchType" label="検索対象" options={searchTypeOptions} defaultValue="sku" />
            <QueryInput paramName="keyword" label="検索キーワード" />
          </div>
        </div>
        <div className="d-flex justify-content-between align-items-end">
          {
            isSelecting ? (
              <div className="mb-3 d-flex justify-content-end align-items-center">
                <div className="d-flex align-items-end">
                  <div>
                    {selectedItemIds.length} 件を選択中
                  </div>
                  <ProgressButton className="ml-2" process={onClickPrintReceivementLabels}>
                    入荷ラベル
                  </ProgressButton>
                </div>
              </div>
            ) : <div />
          }
          <div className="d-flex justify-content-end align-items-end mb-2 gap-1">
            <Pagination {...pagination} />
            <Button onClick={onClickDownloadSampleCsv}>
              <span className="fas fa-download mr-1" />
              サンプルCSVダウンロード
            </Button>
            <ExportButton fileName="入荷依頼.csv" rows={rowsForExport} />
            <ImportButton label="更新インポート" processRows={processUpdateRows} disabled={!hasLoadedReceivingRequestChunks} />
            <ImportButton processRows={processRows} disabled={!hasLoadedReceivingRequestChunks} />
            <AddButton
              itemRef={newRef}
              FormModal={ReceivingRequestFormModal}
              processValues={_ => ({ ..._, status: 'initial', productIds: _.items.map(_ => _.productId), })}
              disabled={!hasLoadedReceivingRequestChunks}
              formProps={{
                values: {},
                tenant,
                products,
                newProductId,
                renderFormBody: (base) => {
                  return (
                    <Fragment>
                      <Field type="string" label="入荷ID" value={newRef && idWithTenant(newRef)} readOnly={_ => true} />
                      {base}
                    </Fragment>
                  );
                },
                renderFooterContent: (base, { items, onSubmit, isUnsubmittable, isSubmitting }) => {
                  const productSelectedItems = items.filter(_ => _.productId);
                  const isAllAmazonProduct = productSelectedItems.length > 0 && productSelectedItems.every(_ => get(productsById, [_.productId, 'isAmazonProduct']));
                  const isAllNotAmazonProduct = productSelectedItems.length > 0 && productSelectedItems.every(_ => !get(productsById, [_.productId, 'isAmazonProduct']));
                  const _onSubmit = async (event) => {
                    await onSubmit(event);
                    history.push(`/shippingRequests?${qs.stringify({ newItems: items.map(_ => ({ productId: _.productId, goodQuantity: _.quantity })), newType: 'normal' })}`);
                  };
                  return (
                    <Fragment>
                      {base}
                      <Button className="save" type="submit" color="primary" onClick={_onSubmit} disabled={isUnsubmittable || isSubmitting}>このまま出荷依頼する</Button>
                    </Fragment>
                  );
                },
              }}
              // DEV
              // opensInitially
              opensInitially={newProductId != null}
            />
          </div>
        </div>
        <div className="overflow-auto position-relative" style={{ zIndex: 0 }}>
          {
            sortedReceivingRequestItems.length > 0 ? (
              <table className="table sticky-table table-sm table-bordered">
                <thead className="thead-light text-center">
                  <tr className="text-nowrap">
                    <th style={{ minWidth: 30 }}>
                      <Input type="checkbox" className="position-relative m-0" checked={isAllDisplayedSelected} onChange={_ => setSelectedItems(_.target.checked ? union(selectedItemIds, displayedItemIds) : difference(selectedItemIds, displayedItemIds))} />
                    </th>
                    <th style={{ minWidth: 80 }}>
                      <QuerySortLabel field="id">
                        入荷ID
                      </QuerySortLabel>
                      <br />
                      入荷依頼日
                      <br />
                      <QuerySortLabel field="scheduleDate">
                        入荷予定日
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 80 }}>
                      <QuerySortLabel field="status">
                        ステータス
                      </QuerySortLabel>
                      <br />
                      <QuerySortLabel field="receivedAt">
                        入庫日
                      </QuerySortLabel>
                      <br />
                      <QuerySortLabel field="documentNumber">
                        伝票番号
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 120 }}>
                      <QuerySortLabel field="unknown">
                        不明
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 200 }}>商品</th>
                    <th style={{ minWidth: 80 }}>
                      入庫依頼数
                      <br />
                      入庫数
                      <br />
                      販売不可数
                    </th>
                    <th style={{ minWidth: 100 }}>
                      <QuerySortLabel field="receivingOptionValues">
                        オプション
                      </QuerySortLabel>
                    </th>
                    <th style={{ minWidth: 100 }}>検品依頼</th>
                    <th style={{ minWidth: 100 }}>セット内容</th>
                    <th style={{ minWidth: 150 }}>
                      メモ
                      <br />
                      連絡メモ
                    </th>
                    <th style={{ minWidth: 100 }}>添付ファイル</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {
                    sortedReceivingRequestItems.map((receivingRequestItem) => {
                      const { id, ref, pendingItems = [], items = [], documentNumber, status, note, tenantNote, files = [], slipImages = [], janImages = [], receivingOptionValues = [], checkNote, setNote, viaCancel = false, createdAt, receivedAt, scheduleDate, isReceivedByUser = false, isCreatedBySystem = false, } = receivingRequestItem;
                      const isImmutable = ['processing', 'received'].includes(status);
                      const rowItems = items.length > 0 ? items : [{}];
                      const { color } = statuses[status];
                      const processValues = (values) => {
                        return {
                          ...values,
                          productIds: values.items.map(_ => _.productId),
                          status: ({
                            unknown: values.items.length > 0 && 'resubmitted',
                            pending: 'resubmitted',
                          })[status] || status,
                        };
                      };

                      return (
                        <Fragment key={id}>
                          {
                            rowItems.map((item, i) => {
                              const { productId, quantity, } = item;
                              const goodInventoryIds = receivingRequestItem[`goodInventoryIds-${productId}`] || [];
                              const ngInventoryIds = receivingRequestItem[`ngInventoryIds-${productId}`] || [];
                              const product = productsById[productId];
                              //                              const onClickConvertToGoodQuantity = async () => {
                              //                                if(!window.confirm('本当に販売可にしますか？')) return;
                              //
                              //                                try {
                              //                                  ref.update({
                              //                                    items: [
                              //                                      ...items.slice(0, i),
                              //                                      {
                              //                                        ...items[i],
                              //                                        ngQuantity: 0,
                              //                                        goodQuantity: goodQuantity + ngQuantity,
                              //                                      },
                              //                                      ...items.slice(i + 1),
                              //                                    ],
                              //                                  });
                              //                                } catch (e) {
                              //                                  console.error(e);
                              //                                  toast.error('失敗しました');
                              //                                }
                              //                              };

                              return (
                                <tr key={i} className={`table-${color}`}>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          <Input type="checkbox" className="position-relative m-0" checked={selectedItemIds.includes(id)} onChange={_ => selectedItemIds.includes(id) ? unselectItem(id) : selectItem(id)} />
                                        </td>
                                        <td rowSpan={rowItems.length} className="text-break" style={{ minWidth: 200 }}>
                                          <div className="d-flex align-items-center gap-1">
                                            <ModalButton
                                              Component="span"
                                              className="text-primary cursor-pointer"
                                              label={<u>{idWithTenant(ref)}</u>}
                                              title={idWithTenant(ref)}
                                              content={() => {
                                                return (
                                                  <ReceivingRequestDetail receivingRequest={receivingRequestItem} productsById={productsById} />
                                                );
                                              }}
                                            />
                                            {isCreatedBySystem && <div className="badge badge-warning">倉庫作成分</div>}
                                          </div>
                                          <div className="text-muted">{formatDate(createdAt.toDate(), 'yyyy/MM/dd')}</div>
                                          <div className="text-muted">{scheduleDate != null && formatDate(scheduleDate.toDate(), 'yyyy/MM/dd')}</div>
                                          {viaCancel && <div className="badge badge-secondary text-wrap">出荷キャンセルによる返送分 {tenant.id}-{receivingRequestItem.shippingRequestId}</div>}
                                        </td>
                                        <td className="text-break" rowSpan={rowItems.length}>
                                          <div>
                                            {statuses[status].label}
                                            {isReceivedByUser && <span className="badge badge-warning">ユーザー対応</span>}
                                          </div>
                                          <div>
                                            {receivedAt && formatDate(receivedAt.toDate(), 'yyyy/MM/dd')}
                                          </div>
                                          <div>{documentNumber}</div>
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            pendingItems.map((pendingItem, i) => {
                                              const { boxSize, quantity } = pendingItem;
                                              return (
                                                <div key={i}>
                                                  {boxSizes[boxSize].label} {quantity}個
                                                </div>
                                              );
                                            })
                                          }
                                          {
                                            [...slipImages, ...janImages].map((file) => {
                                              return (
                                                <div className="mt-1">
                                                  <a href={file.downloadUrl} target="_blank">
                                                    <img src={file.downloadUrl}  style={{ maxWidth: 100 }}/>
                                                  </a>
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                  <td>
                                    <ProductTagsDisplay product={product} productTags={[]} />
                                    <div className="text-muted small d-flex gap-1">
                                      <div>{product && product.sku}</div>
                                    </div>
                                    <div className="d-flex align-items-start gap-1">
                                      {
                                        product?.isAmazonProduct && (
                                          <div>
                                            <img src={smallImageUrl(product?.amazonProduct)} style={{ maxHeight: 40 }} />
                                          </div>
                                        )
                                      }
                                      <div className="flex-grow-1 text-wrap">
                                        <span>{product?.name}</span>
                                      </div>
                                    </div>
                                    <div>
                                      {
                                        (receivingRequestItem[`ngFileDownloadUrls_${product?.id}`] || []).map((downloadUrl) => {
                                          return (
                                            <div className="mt-1">
                                              <a href={downloadUrl} target="_blank">
                                                <img src={downloadUrl}  style={{ maxWidth: 50 }}/>
                                              </a>
                                            </div>
                                          );
                                        })
                                      }
                                    </div>
                                  </td>
                                  <td className="text-right">
                                    <div>{numeral(quantity).format('0,0')}</div>
                                    <div>{numeral(goodInventoryIds.length).format('0,0')}</div>
                                    <div>{numeral(ngInventoryIds.length).format('0,0')}</div>
                                    {
                                      false && status === 'received' && ngInventoryIds.length > 0 && (
                                        <Button color="success" onClick={_ => _} size="sm" className="sm p-1 d-none">
                                          <span className="small">販売可にする</span>
                                        </Button>
                                      )
                                    }
                                  </td>
                                  {
                                    i === 0 && (
                                      <Fragment>
                                        <td rowSpan={rowItems.length}>
                                          {fieldDisplayValue(receivingOptionValues, fields().receivingOptionValues)}
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          {checkNote}
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          {setNote}
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          <div className="border rounded p-2" style={{ minHeight: 30 }}>
                                            <div>
                                              <EditButton size="sm" color="link" label={false} itemRef={ref} FormModal={ModelFormModal} formProps={{ title: 'メモ', fields: { tenantNote: { rows: 10, label: 'メモ', hint: '自社確認用', type: 'text' } }, }} onFinish={updateComponentVersion} />
                                            </div>
                                            {tenantNote}
                                          </div>
                                          {
                                            note && (
                                              <div className="mt-1 border rounded p-2" style={{ minHeight: 30 }}>
                                                {note}
                                              </div>
                                            )
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length} style={{ whiteSpace: 'pre-line' }}>
                                          {
                                            files.map((file) => {
                                              return (
                                                <div className="mt-1">
                                                  {file.name}
                                                  <a className="ml-2" href={file.downloadUrl} target="_blank">
                                                    <span className="fas fa-eye" />
                                                  </a>
                                                </div>
                                              );
                                            })
                                          }
                                        </td>
                                        <td rowSpan={rowItems.length}>
                                          {
                                            !viaCancel && (
                                              <div className="d-flex flex-column gap-1 text-nowrap">
                                                <EditButton size="sm" itemRef={ref} FormModal={ReceivingRequestFormModal} processValues={processValues} formProps={{ tenant, values: receivingRequestItem, products, }} disabled={isImmutable}  onFinish={updateComponentVersion} />
                                                <DeleteButton size="sm" item={receivingRequestItem} itemRef={ref} disabled={isImmutable || status === 'unknown'}  onFinish={updateComponentVersion} />
                                              </div>
                                            )
                                          }
                                        </td>
                                      </Fragment>
                                    )
                                  }
                                </tr>
                              );
                            })
                          }
                        </Fragment>
                      );
                    })
                  }
                </tbody>
              </table>
            ) : (
              <div>
                入荷依頼は未登録です
              </div>
            )
          }
        </div>
      </div>
    </div>
  );
});
